// Reporting-app cascade actions.
//
// Selecting a new region/org/team in the reporting app cascades downstream:
// region → clears org + team + session, org → clears team + session,
// team → clears session. An auto-select-first effect in useReportingAutoSelect
// then repopulates from the next query.
//
// These are reporting-specific: other apps using region/org don't cascade into
// team/session, so the cascade lives here rather than in the shared slices.
//
// We also reset the RTK Query caches for the scopes that change: otherwise
// the auto-select effect would immediately repopulate from stale cached data
// (teams/orgs from the old region) — users would see the old selections
// stick around until a manual refetch.
import type { AppThunk } from '@frontend/core'
import { managementApi, reportingApi } from '@frontend/core'
import {
  setReportingSessionId,
  setReportingTeamId
} from '@frontend/core/lib/domains/reporting'
import { setSelectedOrganisationId } from '@frontend/core/lib/domains/organisations'
import { setSelectedRegionId } from '@frontend/core/lib/domains/regions'
import type { regions as regionsData } from '@frontend/core/lib/domains/regions/data_types'

export const switchReportingTeam =
  (teamId: string | null): AppThunk =>
  (dispatch) => {
    dispatch(setReportingTeamId(teamId))
    dispatch(setReportingSessionId(null))
  }

export const switchReportingSession =
  (sessionId: string | null): AppThunk =>
  (dispatch) => {
    dispatch(setReportingSessionId(sessionId))
  }

export const switchReportingOrganisation =
  (organisationId: string | null): AppThunk =>
  (dispatch) => {
    dispatch(setSelectedOrganisationId(organisationId))
    dispatch(setReportingTeamId(null))
    dispatch(setReportingSessionId(null))
    // Teams + sessions are org-scoped — dump the cached results so the
    // auto-select effect waits for the fresh data before picking.
    dispatch(reportingApi.util.resetApiState())
  }

export const switchReportingRegion =
  (regionId: keyof typeof regionsData | null): AppThunk =>
  (dispatch) => {
    dispatch(setSelectedRegionId(regionId))
    dispatch(setSelectedOrganisationId(null))
    dispatch(setReportingTeamId(null))
    dispatch(setReportingSessionId(null))
    // Cache invalidation — region changes the API base URL, so every
    // cached response from the old region is stale. We use `invalidateTags`
    // rather than `resetApiState()` because `resetApiState` aborts any
    // in-flight request at the same time, and the subscribed hooks pick
    // that abort up as a FETCH_ERROR on their very next refetch. Tag
    // invalidation marks entries stale and lets the next fetch complete
    // normally.
    dispatch(
      managementApi.util.invalidateTags([{ type: 'organisations', id: 'LIST' }])
    )
    dispatch(
      reportingApi.util.invalidateTags([
        'reportingTeams',
        'reportingPlayers',
        'reportingSessions',
        'sessionSummary',
        'playerSummary',
        'playerActions'
      ])
    )
  }
